import moment from 'moment';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Button from '../../componants/Base/Button';
import Input from '../../componants/Base/Input';
import Switch from '../../componants/Base/Switch';
import useFetch from '../../hooks/useFetch';
import useNotification from '../../hooks/useNotification';
import useSocket from '../../hooks/useSocket';

const STREAM_BASE = process.env.REACT_APP_STREAM_BASE_URL || 'https://swiss1.streamset.pro';

const Session = () => {
	const location = useLocation();
	const { postReq } = useFetch();
	const { getSocket, socketLoading, setSocketLoading } = useSocket();
	const navigate = useNavigate();
	const { mountNote } = useNotification();

	const [streams, setStreams] = useState([]);
	const [loading, setLoading] = useState(false);
	const [copiedId, setCopiedId] = useState(null);

	const copyWhipUrl = (player) => {
		const url = `${STREAM_BASE}/${values._id}-${player.id}/whip`;
		const onSuccess = () => {
			setCopiedId(player.id);
			mountNote('success', 'WHIP URL copied to clipboard');
			setTimeout(() => setCopiedId(null), 2000);
		};
		if (navigator.clipboard?.writeText) {
			navigator.clipboard.writeText(url).then(onSuccess).catch(() => {
				fallbackCopy(url);
				onSuccess();
			});
		} else {
			fallbackCopy(url);
			onSuccess();
		}
	};

	const fallbackCopy = (text) => {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.style.position = 'fixed';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.select();
		document.execCommand('copy');
		document.body.removeChild(ta);
	};
	const [values, setValues] = useState({
		name: '',
		inizio: moment().format('YYYY-MM-DDThh:mm'),
		fine: moment().add(3, 'months').format('YYYY-MM-DDThh:mm'),
		hasChat: false,
		hasAudio: false,
		logo: '',
		players: streams,
	});

	const updateStream = (field, value, index) => {
		let players = [...values.players];
		players.map((player) =>
			players.indexOf(player) === index
				? (player[field] = value)
				: player[field],
		);
		setStreams(players);
	};

	const submit = async () => {
		setLoading(true);
		// Ensure inizio and fine are proper ISO strings with timezone
		const payload = {
			...values,
			inizio: moment(values.inizio).toISOString(),
			fine: moment(values.fine).toISOString(),
		};
		let url = 'sessions/' + values._id;
		let res = await postReq(url, payload);
		if (res !== 'error') {
			mountNote('success', 'Session updated successfully');
		}
		setLoading(false);
	};

	useEffect(() => {
		setValues({ ...values, players: streams }); // eslint-disable-next-line
	}, [streams]);

	useEffect(() => {
		const getSessionWithSocket = () => {
			const socket = getSocket(
				`${process.env.REACT_APP_BASE_WSS_URL}sessions/${location.state._id}`,
			);

			socket.onmessage = (message) => {
				let response;
				setSocketLoading(false);
				try {
					response = JSON.parse(message.data);
					if (response.shape === 'virtualset.v1.session') {
						let data = response.session;

						setValues({
							name: data.name,
							_id: data._id,
							inizio: data.inizio,
							fine: data.fine,
							hasChat: data.hasChat,
							hasAudio: data.hasAudio,
							accessCode: data.id,
							directorId: data.directorId,
							privateId: data.privateId,
							logo: data.logo,
							players: data.players,
						});
					}
				} catch (error) {
					return;
				}
			};

			return ()=>{
				socket.onclose = ()=>{};
				socket.close();
			}
		};
		if (location.state) {
			return getSessionWithSocket();
		} else {
			navigate('/sessions');
		} // eslint-disable-next-line
	}, [location]);

	return (
		<div className={`editSession ${socketLoading ? 'containerLoading' : ''}`}>
			<h1 className='editSession__title'>
				<span>Edit</span> Session
			</h1>
			<form className='editSession__grid'>
				{/* Session Details */}
				<div className='card'>
					<div className='card__header'>
						<h3>Session Details</h3>
					</div>
					<Input
						containerStyle={{ marginBottom: 15 }}
						title='Name'
						type='text'
						value={values.name}
						onChange={(e) => setValues({ ...values, name: e })}
					/>
					<div className='card__divider' />
					<div className='card__header'>
						<h3>Access Codes</h3>
					</div>
					<div className='accessCode'>
						<span className='accessCode__label'>Standard</span>
						<span className='accessCode__value'>{values.accessCode}</span>
					</div>
					<div className='accessCode'>
						<span className='accessCode__label'>Director</span>
						<span className='accessCode__value'>{values.directorId}</span>
					</div>
					<div className='accessCode'>
						<span className='accessCode__label'>Incognito</span>
						<span className='accessCode__value'>{values.privateId}</span>
					</div>
				</div>

				{/* Features & Schedule */}
				<div className='card'>
					<div className='card__header'>
						<h3>Features & Schedule</h3>
					</div>
					<Switch
						title='Text Chat'
						checked={values.hasChat || false}
						onChange={(e) => setValues({ ...values, hasChat: e })}
					/>
					<Switch
						title='Audio-Video Chat'
						checked={values.hasAudio || false}
						onChange={(e) => setValues({ ...values, hasAudio: e })}
					/>
					<div className='card__divider' />
					<div className='dateRow'>
						<Input
							title='Inizio'
							value={moment(values.inizio).format('YYYY-MM-DDThh:mm')}
							type='datetime-local'
							onChange={(e) => setValues({ ...values, inizio: e })}
						/>
						<Input
							title='Fine'
							value={moment(values.fine).format('YYYY-MM-DDThh:mm')}
							type='datetime-local'
							onChange={(e) => setValues({ ...values, fine: e })}
						/>
					</div>
					<div className='card__divider' />
					<Input
						title='Logo (absolute url with https://)'
						value={values.logo}
						type='url'
						onChange={(e) =>
							setValues({
								...values,
								logo: e,
							})
						}
					/>
				</div>

				{/* Streams - Full Width */}
				<div className='card editSession__fullWidth'>
					<div className='card__header'>
						<h3>Streams</h3>
						{values.players && values.players.length > 0 && (
							<span className='card__badge'>{values.players.length}</span>
						)}
					</div>
					<div className='streamsGrid'>
						{values.players &&
							values.players.length > 0 &&
							values.players.map((stream, i) => (
								<div className='streamCard' key={i}>
									<div className='streamCard__header'>Stream {i + 1}</div>
									<div className='streamCard__inputRow'>
										<Input
											value={stream.name}
											containerStyle={{ width: '100%', marginBottom: 0 }}
											title='Name'
											onChange={(e) => updateStream('name', e, i)}
										/>
										<button
											type='button'
											title='Copy WHIP URL for OBS'
											onClick={() => copyWhipUrl(stream)}
											className='copyWhipBtn'>
											{copiedId === stream.id ? (
												<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='#4CAF50' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
													<polyline points='20 6 9 17 4 12' />
												</svg>
											) : (
												<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
													<rect x='9' y='9' width='13' height='13' rx='2' ry='2' />
													<path d='M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1' />
												</svg>
											)}
										</button>
									</div>
									<div className='streamCard__switches'>
										<Switch
											checked={stream.director || false}
											title='Director only'
											onChange={(e) => updateStream('director', e, i)}
										/>
										<Switch
											checked={stream.hidden || false}
											title='Hidden'
											onChange={(e) => updateStream('hidden', e, i)}
										/>
									</div>
								</div>
							))}
					</div>
				</div>
			</form>
			<div className='actionBar'>
				<Button
					title='ENCODER'
					func={() => navigate('/linkEncoder/' + location.state._id)}
				/>
				<Button title='SAVE' loading={loading} filled func={submit} />
			</div>
		</div>
	);
};

export default Session;
